import axios from 'axios';
import { Store } from 'vuex';
import VueRouter from 'vue-router';
import TranslationService from '@/locale/translation.service';
import { ProcessRole } from './process-role.enum';

export default class AccountService {
  /** Shared promise to deduplicate concurrent retrieveAccount() calls. */
  private retrieveAccountPromise: Promise<boolean> | null = null;

  constructor(private store: Store<any>, private translationService: TranslationService, private router: VueRouter) {
    this.init();
  }

  public init(): void {
    this.retrieveProfiles();
  }

  public retrieveProfiles(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get<any>('management/info')
        .then(res => {
          if (res.data && res.data.activeProfiles) {
            this.store.commit('setRibbonOnProfiles', res.data['display-ribbon-on-profiles']);
            this.store.commit('setActiveProfiles', res.data['activeProfiles']);
          }
          resolve(true);
        })
        .catch(() => resolve(false));
    });
  }

  public retrieveAccount(): Promise<boolean> {
    if (this.retrieveAccountPromise) {
      return this.retrieveAccountPromise;
    }
    this.retrieveAccountPromise = new Promise(resolve => {
      axios
        .get<any>('api/account')
        .then(response => {
          this.store.commit('authenticate');
          const account = response.data;
          if (account) {
            this.store.commit('authenticated', account);
            if (this.store.getters.currentLanguage !== account.langKey) {
              this.store.commit('currentLanguage', account.langKey);
            }
            if (sessionStorage.getItem('requested-url')) {
              this.router.replace(sessionStorage.getItem('requested-url'));
              sessionStorage.removeItem('requested-url');
            }
          } else {
            this.store.commit('logout');
            if (this.router.currentRoute.path !== '/') {
              this.router.push('/');
            }
            sessionStorage.removeItem('requested-url');
          }
          this.translationService.refreshTranslation(this.store.getters.currentLanguage);
          resolve(true);
        })
        .catch(() => {
          this.store.commit('logout');
          resolve(false);
        })
        .finally(() => {
          this.retrieveAccountPromise = null;
        });
    });
    return this.retrieveAccountPromise;
  }

  public hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    if (!this.authenticated || !this.userAuthorities) {
      const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
      if (!this.store.getters.account && !this.store.getters.logon && token) {
        return this.retrieveAccount().then(resp => {
          if (resp) {
            return this.checkAuthorities(authorities);
          }
          return Promise.resolve(false);
        });
      }
      return Promise.resolve(false);
    }

    return this.checkAuthorities(authorities);
  }

  public get authenticated(): boolean {
    return this.store.getters.authenticated;
  }

  public get userAuthorities(): any {
    return this.store.getters.account?.authorities;
  }

  private checkAuthorities(authorities: any): Promise<boolean> {
    if (this.userAuthorities) {
      for (const authority of authorities) {
        if (this.userAuthorities.includes(authority)) {
          return Promise.resolve(true);
        }
      }
    }
    return Promise.resolve(false);
  }

  public hasAnyAuthority(authorities: any): boolean {
    if (!this.authenticated || !this.userAuthorities) {
      return false;
    }

    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    for (let i = 0; i < authorities.length; i++) {
      if (this.userAuthorities.includes(authorities[i])) {
        return true;
      }
    }

    return false;
  }

  public isAnalyst(): boolean {
    return this.hasAnyAuthority(ProcessRole.ROLE_ANALYSTE);
  }

  public isAnalystControler(): boolean {
    return this.hasAnyAuthority(ProcessRole.ROLE_ANALYSTE_CONTROLER);
  }

  public isGPSN(): boolean {
    return this.hasAnyAuthority(ProcessRole.ROLE_CONTROLE);
  }

  public isExpert(): boolean {
    return this.hasAnyAuthority(ProcessRole.ROLE_EXPERT);
  }

  public isGTD(): boolean {
    return this.hasAnyAuthority(ProcessRole.ROLE_GTD);
  }

  public getCurrentProcessRole(): ProcessRole {
    for (const auth of Object.values(ProcessRole)) {
      if (this.hasAnyAuthority(auth)) {
        return auth;
      }
    }
    return null;
  }

  /**
   * Get all process roles for the current user.
   * Returns array of all matching roles instead of just the first match.
   * Use this for multi-role permission checking with union semantics.
   * @returns Array of ProcessRole values the user has
   */
  public getAllProcessRoles(): ProcessRole[] {
    const roles: ProcessRole[] = [];
    for (const auth of Object.values(ProcessRole)) {
      if (this.hasAnyAuthority(auth)) {
        roles.push(auth);
      }
    }
    return roles;
  }
}
